import React, { useState, useEffect } from 'react';  
import { Link } from 'react-router-dom';
import { 
  FiAlertTriangle, FiCheck, FiClock, FiDownload, 
  FiBell, FiSend, FiDollarSign, FiRefreshCw 
} from 'react-icons/fi';  
import { leaseAPI, paymentAPI, smsAPI } from '../../services/api';  
import DataTable from '../../components/shared/DataTable';  
import StatCard from '../../components/shared/StatCard';
import Modal from '../../components/shared/Modal';

const PaymentListPage = () => {  
  const [tab, setTab] = useState('overdue');  
  const [overdue, setOverdue] = useState([]);  
  const [loading, setLoading] = useState(true);  
  
  // Wallet SMS de l'agence
  const [smsWallet, setSmsWallet] = useState(null);

  // Historique & Vérification
  const [allPayments, setAllPayments] = useState([]);  
  const [loadingAll, setLoadingAll] = useState(false);  
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // Relance en cours
  const [notifyingId, setNotifyingId] = useState(null);
  const [notifyingAll, setNotifyingAll] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState(null);

  // Modal SMS BatakiPlus
  const [smsModal, setSmsModal] = useState(null);
  const [smsForm, setSmsForm] = useState({
    phone: '',
    name: '',
    message: '',
  });
  const [sendingSms, setSendingSms] = useState(false);

  // Modal Saisie Paiement
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: '',
    payment_method_id: '',
    reference: '',
    paid_date: new Date().toISOString().split('T')[0],
  });

  const fetchOverdue = async () => {
    setLoading(true);
    try {
      const r = await leaseAPI.getOverduePayments();
      setOverdue(r.data || []);
    } catch (e) {
      console.error('Erreur chargement paiements en retard:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPayments = () => {  
    setLoadingAll(true);  
    leaseAPI.getAllPayments({ status: 'paid' })  
      .then(r => { setAllPayments(r.data || []); setLoadingAll(false); })  
      .catch(() => setLoadingAll(false));  
  };  

  const fetchPendingPayments = () => {
    setLoadingPending(true);
    paymentAPI.listAgencyPayments()
      .then(r => {
        const pending = (r.data || []).filter(p => p.verification_status === 'pending');
        setPendingPayments(pending);
        setLoadingPending(false);
      })
      .catch(() => setLoadingPending(false));
  };

  const fetchSmsWallet = async () => {
    try {
      const res = await smsAPI.getWallet();
      setSmsWallet(res.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    fetchOverdue();
    fetchSmsWallet();
  }, []);

  useEffect(() => {  
    if (tab === 'history') fetchAllPayments();  
    if (tab === 'verification') fetchPendingPayments();
    if (tab === 'overdue') { fetchOverdue(); fetchSmsWallet(); }
  }, [tab]);

  // Ouvrir modal SMS
  const handleOpenSmsModal = (payment) => {
    const tenantName = `${payment.tenant_first_name || ''} ${payment.tenant_last_name || ''}`.trim();
    const amount = Number(payment.amount || 0).toLocaleString('fr-FR');
    const dueDateObj = new Date(payment.due_date);
    const endMonth = new Date(dueDateObj.getFullYear(), dueDateObj.getMonth() + 1, 0);
    const daysOverdue = Math.max(1, Math.floor((new Date() - endMonth) / 86400000));
    const agencyName = payment.agency_name || 'Votre Agence Immobilière';

    const defaultMsg = `Bonjour ${tenantName || 'Cher Locataire'}, sauf erreur, votre loyer de ${amount} XOF pour ${payment.property_title || 'votre logement'} (${daysOverdue}j de retard) est impayé. Merci de régulariser votre situation auprès de ${agencyName}.${payment.agency_phone ? ` Tél: ${payment.agency_phone}` : ''}`;

    setSmsModal(payment);
    setSmsForm({
      phone: payment.tenant_phone || '',
      name: tenantName || 'Locataire',
      message: defaultMsg,
    });
  };

  // Envoi SMS via BatakiPlus
  const handleSendSms = async (e) => {
    e.preventDefault();
    if (!smsModal) return;
    setSendingSms(true);
    setNotificationMsg(null);
    try {
      const res = await leaseAPI.sendPaymentSMS(smsModal.id, smsForm);
      setNotificationMsg({
        type: 'success',
        text: res.data?.message || `SMS envoyé avec succès à ${smsForm.name} (${smsForm.phone}) !`
      });
      setSmsModal(null);
      fetchOverdue();
      fetchSmsWallet();
    } catch (err) {
      setNotificationMsg({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'envoi du SMS via BatakiPlus.'
      });
    } finally {
      setSendingSms(false);
    }
  };

  // Relancer un locataire en retard
  const handleSingleRelance = async (payment) => {
    setNotifyingId(payment.id);
    setNotificationMsg(null);
    try {
      const res = await leaseAPI.notifyOverduePayment(payment.id, { sendSms: true, sendEmailNotify: true });
      const smsStatus = res.data?.smsSent ? ' • SMS BatakiPlus ✅' : (res.data?.smsSimulated ? ' • SMS simulé 📱' : '');
      const emailStatus = res.data?.emailSent ? ' • Email ✅' : '';
      setNotificationMsg({
        type: 'success',
        text: `Relance envoyée avec succès à ${payment.tenant_first_name} ${payment.tenant_last_name} ! (${emailStatus}${smsStatus} • Push & In-App)`
      });
      fetchOverdue();
      fetchSmsWallet();
    } catch (err) {
      setNotificationMsg({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'envoi de la relance.'
      });
    } finally {
      setNotifyingId(null);
    }
  };

  // Relance globale
  const handleNotifyAllOverdue = async () => {
    if (!window.confirm(`Voulez-vous envoyer une notification générale de relance à l'ensemble des ${overdue.length} locataires en retard de paiement ?`)) return;
    
    setNotifyingAll(true);
    setNotificationMsg(null);
    try {
      const res = await leaseAPI.notifyAllOverduePayments({ sendSms: true, sendEmailNotify: true });
      setNotificationMsg({
        type: 'success',
        text: `🚀 Relance générale effectuée ! ${res.data?.count || overdue.length} relances ont été envoyées (SMS BatakiPlus, Email, Push et In-App).`
      });
      fetchOverdue();
      fetchSmsWallet();
    } catch (err) {
      setNotificationMsg({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de la relance générale.'
      });
    } finally {
      setNotifyingAll(false);
    }
  };

  const handleVerify = async (paymentId, status) => {
    try {
      await paymentAPI.verifyPayment(paymentId, { status });
      fetchPendingPayments();
    } catch (err) {
      alert("Erreur lors de la vérification du paiement.");
    }
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    if (!payModal) return;
    try {
      await leaseAPI.markPaymentPaid(payModal.id, payForm);
      setPayModal(null);
      fetchOverdue();
      alert('Paiement enregistré avec succès !');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du règlement.');
    }
  };

  const [downloadingReceiptId, setDownloadingReceiptId] = React.useState(null);

  const handleDownloadReceipt = async (payment) => {
    setDownloadingReceiptId(payment.id);
    try {
      const res = await leaseAPI.downloadRentReceipt(payment.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const tenantName = `${payment.tenant_last_name || payment.tenant_first_name || 'locataire'}`.toUpperCase();
      const period = payment.due_date
        ? new Date(payment.due_date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(' ', '-')
        : 'quittance';
      a.download = `Quittance-${period}-${tenantName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors de la génération de la quittance PDF.');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const exportCSV = (data, filename) => {  
    const headers = ['Locataire', 'Email', 'Téléphone', 'Bien', 'Contrat', 'Montant', 'Échéance', 'Statut', 'Notes'];  
    const rows = data.map(r => [  
      `${r.tenant_first_name || ''} ${r.tenant_last_name || ''}`.trim(),  
      r.tenant_email || '', r.tenant_phone || '',
      r.property_title || '', r.contract_number || '',  
      parseInt(r.amount || 0), r.due_date || '', r.status || '', r.notes || '' 
    ]);  
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');  
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });  
    const url = URL.createObjectURL(blob);  
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();  
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // Calculs KPI Retards
  const totalOverdueAmount = overdue.reduce((acc, p) => acc + parseFloat(p.amount || 0), 0);
  const avgDaysOverdue = overdue.length > 0
    ? Math.round(overdue.reduce((acc, p) => {
        const dObj = new Date(p.due_date);
        const endMonth = new Date(dObj.getFullYear(), dObj.getMonth() + 1, 0);
        return acc + Math.max(1, Math.floor((new Date() - endMonth) / 86400000));
      }, 0) / overdue.length)
    : 0;

  // Colonnes Retards
  const overdueColumns = [  
    { 
      header: 'Locataire', 
      render: r => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{r.tenant_first_name} {r.tenant_last_name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {r.tenant_email || r.tenant_phone || 'Sans contact'}
          </div>
        </div>
      ) 
    },  
    { 
      header: 'Bien & Contrat', 
      render: r => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.property_title}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Contrat: <code>{r.contract_number}</code></div>
        </div>
      ) 
    },  
    { 
      header: 'Montant Loyer', 
      render: r => (
        <span style={{ fontWeight: 700, color: '#ef4444', fontSize: 13 }}>
          {fmt(r.amount)} XOF
        </span>
      ) 
    },  
    { 
      header: 'Échéance', 
      render: r => <span style={{ fontSize: 12 }}>{fmtDate(r.due_date)}</span>
    },  
    { 
      header: 'Durée Retard', 
      render: r => {  
        const dueDateObj = new Date(r.due_date);
        const endOfDueMonth = new Date(dueDateObj.getFullYear(), dueDateObj.getMonth() + 1, 0);
        const days = Math.max(1, Math.floor((new Date() - endOfDueMonth) / 86400000));  
        return (
          <span className={`badge badge-${days > 30 ? 'danger' : 'warning'}`} style={{ fontWeight: 700, fontSize: 11 }}>
            <FiClock style={{ marginRight: 4 }} /> {days} jour(s)
          </span>
        );  
      }
    },  
    { 
      header: 'Suivi / Relance', 
      render: r => (
        <div style={{ fontSize: 11 }}>
          {r.notes && r.notes.includes('Relance') ? (
            <span className="badge badge-info" style={{ fontSize: 10 }}>
              {r.notes.split('[').pop().replace(']', '')}
            </span>
          ) : (
            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Non relancé</span>
          )}
        </div>
      ) 
    },
    {
      header: 'Actions',
      render: r => (
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
          <button
            className="btn btn-sm"
            onClick={() => handleOpenSmsModal(r)}
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 6,
              padding: '5px 8px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              flexShrink: 0
            }}
            title="Envoyer une alerte par SMS via BatakiPlus"
          >
            <span>📱</span> SMS
          </button>
          <button
            className="btn btn-warning btn-sm"
            onClick={() => handleSingleRelance(r)}
            disabled={notifyingId === r.id}
            style={{ padding: '5px 8px', fontSize: 11, flexShrink: 0 }}
            title="Envoyer une notification de relance multicanale"
          >
            <FiBell style={{ marginRight: 3 }} />
            {notifyingId === r.id ? '...' : 'Relancer'}
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={() => {
              setPayModal(r);
              setPayForm({ amount: r.amount, payment_method_id: '', reference: '', paid_date: new Date().toISOString().split('T')[0] });
            }}
            style={{ padding: '5px 8px', fontSize: 11, flexShrink: 0 }}
            title="Enregistrer le règlement"
          >
            <FiDollarSign style={{ marginRight: 2 }} /> Payer
          </button>
        </div>
      )
    }  
  ];  

  const historyColumns = [  
    { header: 'Locataire', render: r => <span style={{ fontWeight: 600 }}>{r.tenant_first_name} {r.tenant_last_name}</span> },  
    { header: 'Bien', render: r => r.property_title },  
    { header: 'Contrat', render: r => <code>{r.contract_number}</code> },  
    { header: 'Montant', render: r => <strong>{fmt(r.amount)} XOF</strong> },  
    { header: 'Payé le', render: r => fmtDate(r.paid_date) },  
    { header: 'Méthode', render: r => r.payment_method_name || 'Espèces / Virement' },  
    { header: 'Référence', render: r => r.reference || '—' },
    {
      header: 'Quittance',
      render: r => (
        <button
          title="Télécharger la quittance PDF"
          disabled={downloadingReceiptId === r.id}
          onClick={() => handleDownloadReceipt(r)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 8, border: 'none',
            background: downloadingReceiptId === r.id ? '#e2e8f0' : '#eff6ff',
            color: downloadingReceiptId === r.id ? '#94a3b8' : '#1d4ed8',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          <FiDownload size={13} />
          {downloadingReceiptId === r.id ? '…' : 'Quittance'}
        </button>
      ),
    },
  ];

  const verificationColumns = [
    { header: 'Date', render: r => fmtDate(r.created_at || r.payment_date) },
    { header: 'Montant', render: r => <strong>{fmt(r.amount)} XOF</strong> },
    { header: 'Méthode', render: r => r.payment_method_name || '-' },
    { header: 'Référence', render: r => <code>{r.reference || '-'}</code> },
    { header: 'Reçu', render: r => r.receipt_image ? <a href={r.receipt_image} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Voir reçu</a> : '-' },
    { header: 'Action', render: r => (
        <div style={{ display: 'inline-flex', gap: '8px' }}>
          <button className="btn btn-success btn-sm" onClick={() => handleVerify(r.id, 'verified')}><FiCheck /> Valider</button>
          <button className="btn btn-danger btn-sm" onClick={() => handleVerify(r.id, 'rejected')}><FiAlertTriangle /> Rejeter</button>
        </div>
    ) }
  ];

  return (  
    <div className="page-container" style={{ paddingBottom: 40 }}>  
      {/* Header */}
      <div className="page-header">  
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Gestion des Paiements & Relances de Loyers</h1>  
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Surveillez les échéances, notifiez les locataires en retard par SMS BatakiPlus & Email et validez les encaissements
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {tab === 'overdue' && overdue.length > 0 && (
            <button
              className="btn btn-warning btn-sm"
              onClick={handleNotifyAllOverdue}
              disabled={notifyingAll}
              title="Envoyer une relance générale"
            >
              <FiSend style={{ marginRight: 4 }} />
              {notifyingAll ? 'Envoi...' : `⚡ Relance générale (${overdue.length})`}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => exportCSV(tab === 'overdue' ? overdue : allPayments, `paiements-${tab}.csv`)}>  
            <FiDownload style={{ marginRight: 4 }} /> Exporter CSV  
          </button>
        </div>
      </div>  

      {/* Message de notification / feedback */}
      {notificationMsg && (
        <div className="card" style={{
          marginBottom: 20,
          background: notificationMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderLeft: `5px solid ${notificationMsg.type === 'success' ? '#22c55e' : '#ef4444'}`,
          padding: 14
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 600, color: notificationMsg.type === 'success' ? '#15803d' : '#b91c1c', fontSize: 13 }}>
              {notificationMsg.text}
            </span>
            <button className="btn btn-sm btn-secondary" onClick={() => setNotificationMsg(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 24 }}>  
        {[
          { key: 'overdue',      label: `⚠️ Retards & Relances (${overdue.length})` },
          { key: 'verification', label: `⏳ À vérifier (${pendingPayments.length})` },
          { key: 'history',      label: '📋 Historique des règlements' },
        ].map(t => (  
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>  
            {t.label}
          </button>  
        ))}  
      </div>  

      {/* TAB 1: RETARDS DE PAIEMENT & RELANCES */}
      {tab === 'overdue' && (  
        <>
          {/* KPI Cards des retards */}
          <div className="grid-3" style={{ marginBottom: 20 }}>
            <StatCard
              icon={<FiAlertTriangle />}
              label="Loyers en retard"
              value={overdue.length}
              color="#ef4444"
              bgColor="#fee2e2"
            />
            <StatCard
              icon={<FiDollarSign />}
              label="Total des Impayés"
              value={`${fmt(totalOverdueAmount)} XOF`}
              color="#b91c1c"
              bgColor="#fef2f2"
            />
            <StatCard
              icon={<FiClock />}
              label="Retard Moyen"
              value={`${avgDaysOverdue} jour(s)`}
              color="#f59e0b"
              bgColor="#fffbeb"
            />
          </div>

          {/* Banner Canaux Notifs SMS BatakiPlus */}
          <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid #7c3aed', background: '#faf5ff' }}>
            <div className="card-body" style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <strong style={{ color: '#5b21b6', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, flexWrap: 'wrap' }}>
                  <span>📱</span> Passerelle SMS BatakiPlus
                  <span style={{
                    background: (smsWallet?.smsCredits || 0) > 0 ? '#7c3aed' : '#ef4444',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700
                  }}>
                    🪙 {smsWallet ? fmt(smsWallet.smsCredits) : '...'} SMS restants
                  </span>
                </strong>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6d28d9', lineHeight: 1.4 }}>
                  Alertez instantanément vos locataires par <strong>SMS BatakiPlus</strong> directement sur leur téléphone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Link to="/agency/sms-packs" className="btn btn-sm" style={{ background: '#ffffff', color: '#7c3aed', border: '1px solid #7c3aed', fontWeight: 700 }}>
                  + Acheter des SMS
                </Link>
                {overdue.length > 0 && (
                  <button
                    className="btn btn-sm"
                    style={{ background: '#7c3aed', color: '#ffffff', fontWeight: 600 }}
                    onClick={handleNotifyAllOverdue}
                    disabled={notifyingAll}
                  >
                    <FiSend style={{ marginRight: 4 }} /> Relance générale
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tableau des retards */}
          <div className="card">  
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>  
              <h2 style={{ fontSize: 16 }}>Paiements de loyers en retard ({overdue.length})</h2>  
              <button className="btn btn-secondary btn-sm" onClick={fetchOverdue}>
                <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
              </button>
            </div>  
            {loading ? <div className="loading"><div className="spinner" /></div> : (  
              <DataTable columns={overdueColumns} data={overdue} emptyMessage="Aucun retard de paiement actuellement ! Félicitations." />  
            )}  
          </div>  
        </>
      )}  

      {/* TAB 2: VÉRIFICATION DES REÇUS CLIENTS */}
      {tab === 'verification' && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 16 }}>Paiements en attente de vérification par l'agence</h2>
          </div>
          {loadingPending ? <div className="loading"><div className="spinner" /></div> : (
            <DataTable columns={verificationColumns} data={pendingPayments} emptyMessage="Aucun paiement en attente de vérification." />
          )}
        </div>
      )}

      {/* TAB 3: HISTORIQUE DES RÈGLEMENTS */}
      {tab === 'history' && (  
        <div className="card">  
          <div className="card-header">
            <h2 style={{ fontSize: 16 }}>Historique complet des règlements enregistrés</h2>
          </div>
          {loadingAll ? <div className="loading"><div className="spinner" /></div> : (
            <DataTable columns={historyColumns} data={allPayments} emptyMessage="Aucun historique de paiement." />
          )}
        </div>  
      )}  

      {/* MODAL ENVOI SMS BATAKIPLUS */}
      {smsModal && (
        <Modal isOpen={true} onClose={() => setSmsModal(null)} title="📱 Alerte SMS de retard (BatakiPlus)">
          <form onSubmit={handleSendSms}>
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', padding: 12, borderRadius: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <div style={{ fontWeight: 700, color: '#5b21b6' }}>
                  👤 {smsModal.tenant_first_name} {smsModal.tenant_last_name}
                </div>
                <span className="badge badge-warning" style={{ fontSize: 11, fontWeight: 700 }}>
                  ⚠️ {fmt(smsModal.amount)} XOF
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#6d28d9', marginTop: 4 }}>
                🏠 {smsModal.property_title} • Contrat #{smsModal.contract_number}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 11, color: '#4b5563', flexWrap: 'wrap', gap: 4 }}>
                <span>📅 Échéance : {fmtDate(smsModal.due_date)}</span>
                <span style={{ fontWeight: 700, color: (smsWallet?.smsCredits || 0) > 0 ? '#7c3aed' : '#ef4444' }}>
                  🪙 Solde Agence : {smsWallet?.smsCredits || 0} SMS
                </span>
              </div>
            </div>

            {(smsWallet?.smsCredits || 0) <= 0 && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', padding: 10, borderRadius: 8, fontSize: 12, color: '#b91c1c', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <span>⚠️ Vos crédits SMS sont épuisés.</span>
                <Link to="/agency/sms-packs" className="btn btn-sm btn-danger" style={{ fontSize: 11 }}>
                  Acheter des SMS
                </Link>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Nom du destinataire *
              </label>
              <input
                type="text"
                className="form-control"
                required
                value={smsForm.name}
                onChange={(e) => setSmsForm({ ...smsForm, name: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Numéro de téléphone Malien *
              </label>
              <input
                type="tel"
                className="form-control"
                placeholder="Ex: 70123456 ou 66123456"
                required
                value={smsForm.phone}
                onChange={(e) => setSmsForm({ ...smsForm, phone: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  Message SMS de relance *
                </label>
                <span style={{ fontSize: 11, color: smsForm.message.length > 160 ? '#f59e0b' : '#6b7280', fontWeight: 600 }}>
                  {smsForm.message.length} car. ({Math.ceil(smsForm.message.length / 160) || 1} SMS)
                </span>
              </div>
              <textarea
                className="form-control"
                rows={3}
                required
                value={smsForm.message}
                onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setSmsModal(null)}>
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sendingSms || (smsWallet?.smsCredits || 0) <= 0}
                style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
              >
                {sendingSms ? 'Envoi en cours...' : '🚀 Envoyer SMS'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL SAISIE PAIEMENT */}
      {payModal && (
        <Modal isOpen={true} onClose={() => setPayModal(null)} title="Enregistrer un Règlement">
          <form onSubmit={handleMarkPaid}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Montant (XOF) *</label>
              <input
                type="number"
                className="form-control"
                required
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Date de paiement *</label>
              <input
                type="date"
                className="form-control"
                required
                value={payForm.paid_date}
                onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Référence / Reçu</label>
              <input
                type="text"
                className="form-control"
                placeholder="N° chèque, réf virement, etc."
                value={payForm.reference}
                onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setPayModal(null)}>
                Annuler
              </button>
              <button type="submit" className="btn btn-success">
                Valider le paiement
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PaymentListPage;
